// Pipeline tab logic shared by today's tab (PipelineTab) and the full unit
// page (UnitV2/tabs/PipelineTabV2), so the two cannot drift.

export const fmtMoney = (val, digits = 0) => (
    val != null
        ? new Intl.NumberFormat('en-US', {
            style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits,
        }).format(val)
        : '—'
);

export const fmtPipelineDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
        return '—';
    }
};

/**
 * Quote vs asking gap (in %) as one of four bands: up (≥ 0), near (within
 * 5% below), mid (5–10% below), far (more than 10% below), or none.
 */
export const gapBand = (gap) => {
    if (gap == null) return { band: 'none', label: '—' };
    const abs = Math.abs(gap);
    if (gap >= 0) return { band: 'up', label: `+${gap.toFixed(1)}%` };
    if (abs <= 5) return { band: 'near', label: `${gap.toFixed(1)}%` };
    if (abs <= 10) return { band: 'mid', label: `${gap.toFixed(1)}%` };
    return { band: 'far', label: `${gap.toFixed(1)}%` };
};

/** Deal / quote status pill tone: new, present, negotiation, closed, other. */
export const statusTone = (status) => {
    if (!status) return 'other';
    const s = status.toLowerCase();
    if (s === 'new') return 'new';
    if (s.includes('present')) return 'present';
    if (s.includes('negotiation')) return 'negotiation';
    if (s.includes('lost') || s.includes('no sale')) return 'closed';
    return 'other';
};

/** "Buyer quoted within 10% of ask": the average gap within ±10%. */
export const withinTenPct = (avgGap) => avgGap != null && Math.abs(avgGap) <= 10;

/** A single-unit deal whose total meets the AI estimate. */
export const meetsAiEstimate = (deal, aiPrice) => (
    !deal.is_multi_unit && aiPrice != null && deal.quote_total != null && deal.quote_total >= aiPrice
);

/** Is this per-machine quote row for another unit of the same deal? */
export const isOtherUnitRow = (q, stock) => Boolean(q.unit_stock_number) && String(q.unit_stock_number) !== String(stock);

/**
 * Per-machine quotes grouped by deal, plus the orphans: rows without a
 * quote_id or whose deal is neither open nor closed ("Other Quotes").
 */
export function groupMachineQuotes(pipeline, quoteEquipment) {
    const byQuoteId = {};
    const orphans = [];
    (quoteEquipment?.quotes ?? []).forEach((q) => {
        if (q.quote_id) {
            if (!byQuoteId[q.quote_id]) byQuoteId[q.quote_id] = [];
            byQuoteId[q.quote_id].push(q);
        } else {
            orphans.push(q);
        }
    });
    const claimed = new Set();
    (pipeline?.open ?? []).forEach((q) => claimed.add(q.quote_id));
    (pipeline?.lost ?? []).forEach((q) => claimed.add(q.quote_id));
    Object.keys(byQuoteId).forEach((id) => {
        if (!claimed.has(id)) orphans.push(...byQuoteId[id]);
    });
    return { byQuoteId, orphans };
}

/**
 * The quote chart's points: this unit's rows with a price and a date, oldest
 * first; null when fewer than 2. Each point knows its outcome — open (its deal
 * is open), lost, nosale or closed (from the closed deal's status) — and the
 * buyer (N34).
 */
export function quoteChartPoints(quotes, stock, pipeline) {
    const rows = (quotes ?? [])
        .filter((q) => q.unit_stock_number && String(q.unit_stock_number) === String(stock)
            && q.selling_price != null && q.created_date)
        .sort((a, b) => new Date(a.created_date) - new Date(b.created_date));
    if (rows.length < 2) return null;
    const open = new Map((pipeline?.open ?? []).map((d) => [d.quote_id, d]));
    const closed = new Map((pipeline?.lost ?? []).map((d) => [d.quote_id, d]));
    return rows.map((r) => {
        let outcome = 'closed';
        let deal = null;
        if (open.has(r.quote_id)) {
            outcome = 'open';
            deal = open.get(r.quote_id);
        } else if (closed.has(r.quote_id)) {
            deal = closed.get(r.quote_id);
            const s = String(deal.status || '').toLowerCase();
            if (s.includes('no sale')) outcome = 'nosale';
            else if (s.includes('lost')) outcome = 'lost';
        }
        return {
            row: r,
            price: r.selling_price,
            date: r.created_date,
            outcome,
            active: outcome === 'open',
            buyer: deal?.customer || null,
            status: deal?.status || null,
        };
    });
}

export const OUTCOME_LABELS = Object.freeze({ open: 'Active', lost: 'Lost', nosale: 'No sale', closed: 'Closed' });

// Today's InfoTip texts.
export const PIPELINE_TIPS = Object.freeze({
    priceIntel: 'Per-machine quoted selling prices from Salesforce JDQuoteEquipment (Anvil__JDQuoteEquipment__c). Each row is one machine\'s line-item price within a quote — more precise than the deal total because it isolates this unit\'s price even in multi-machine deals.',
    aiEst: 'ML model estimated fair market value for this unit (Azure ML pipeline prediction). Used to flag when a buyer\'s offer already meets or exceeds what the model thinks this unit is worth.',
    avgQuoted: 'Average selling price offered by buyers for this specific unit across all quotes in JDQuoteEquipment. Excludes other machines on the same deal.',
    avgGap: 'Average % difference between buyers\' quoted price and the current asking price. Negative = buyers are offering less than asking. If within −10%, consider calling before dropping the list price.',
    openDeals: 'All Salesforce JDQuote deals for this unit where status ≠ Lost — includes New, Present Solutions, Negotiation/Review, Pending, and any other non-lost status. Deal total covers all machines on the quote (expand multi-unit deals to see the full offer). Per-machine prices from JDQuoteEquipment are nested below each deal.',
    closedDeals: 'Salesforce JDQuote deals classified as closed: Lost, No Sale, Sold, Customer Invoiced, Rental, Demo. Shows the closing status, any recorded lost reason, and the final quoted amount — useful for understanding what price previous buyers walked away at.',
    overallowance: 'Over-allowance reduces effective margin — buyer is receiving more for the trade-in than its retail value',
});

export const PIPELINE_THINKING_STEPS = Object.freeze([
    { label: 'Fetching open quotes', detail: 'Active deals, buyer info, status' },
    { label: 'Loading lost deals', detail: 'Lost quotes, reasons, history' },
    { label: 'Matching equipment line items', detail: 'Per-machine pricing breakdown' },
    { label: 'Building pipeline view', detail: 'Deal timeline and value chart' },
]);
export const PIPELINE_STEP_DURATIONS = Object.freeze([1200, 1000, 1000, 99999]);
